import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "Please confirm",
  description: "",
  confirmText: "Continue",
  cancelText: "Cancel",
  destructive: false,
};

export function ConfirmDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(DEFAULTS);
  const resolverRef = useRef(null);
  const confirmedRef = useRef(false);

  const confirm = useCallback((messageOrOptions) => {
    const opts =
      typeof messageOrOptions === "string"
        ? { ...DEFAULTS, description: messageOrOptions }
        : {
            ...DEFAULTS,
            ...messageOrOptions,
            description: messageOrOptions.description ?? messageOrOptions.message ?? "",
          };

    return new Promise((resolve) => {
      resolverRef.current = resolve;
      confirmedRef.current = false;
      setOptions(opts);
      setOpen(true);
    });
  }, []);

  const finish = useCallback((result) => {
    if (!resolverRef.current) return;
    const resolve = resolverRef.current;
    resolverRef.current = null;
    confirmedRef.current = false;
    setOpen(false);
    resolve(result);
  }, []);

  const handleOpenChange = useCallback(
    (next) => {
      if (!next) finish(confirmedRef.current);
      else setOpen(true);
    },
    [finish],
  );

  const handleConfirm = useCallback(() => {
    confirmedRef.current = true;
    finish(true);
  }, [finish]);

  const handleCancel = useCallback(() => {
    confirmedRef.current = false;
    finish(false);
  }, [finish]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        <AlertDialogContent className="max-w-md rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-bold text-lg">
              {options.title}
            </AlertDialogTitle>
            {options.description ? (
              <AlertDialogDescription className="text-sm text-neutral-600 leading-relaxed">
                {options.description}
              </AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="rounded-sm" onClick={handleCancel}>
              {options.cancelText}
            </Button>
            <Button
              className={cn(
                "rounded-sm font-semibold",
                options.destructive ? "bg-red-600 hover:bg-red-700 text-white" : "btn-brand",
              )}
              onClick={handleConfirm}
            >
              {options.confirmText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within ConfirmDialogProvider");
  }
  return ctx;
}
